/**
 * ホームの上に出す、今月の残りの知らせ（残りが少ない・使い切ったときだけ）
 *
 * プレミアム以外は、ひとつ上のプランへのアップグレードを案内する（料金ページへ）。
 * 回数の目安は料金ページと同じ値（constants/planFeatures.ts の AI_ASSISTANT_ROW）。
 */

import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CHAT_USAGE_LEVEL } from '@/constants/chat';
import { AI_ASSISTANT_ROW } from '@/constants/planFeatures';
import { PLAN_NAME, type PlanType } from '@/constants/planTypes';
import type { ChatUsage } from '@/services/chat';
import {
  chatUpgradePlan,
  chatUsageLevel,
  formatResetDate,
  remainingPercent,
} from '@/utils/chatUsage';

/** 「ベーシックプランにアップグレード（月60回ほど）」 */
export function ChatUpgradeLink({
  plan,
  onNavigate,
  className = '',
}: {
  plan: PlanType;
  onNavigate: () => void;
  className?: string;
}) {
  const estimate = AI_ASSISTANT_ROW[plan];
  return (
    <span className={className}>
      <Link to="/pricing" onClick={onNavigate} className="font-medium underline underline-offset-2">
        {PLAN_NAME[plan]}にアップグレード
      </Link>
      {typeof estimate === 'string' && <span className="whitespace-nowrap">（{estimate}）</span>}
    </span>
  );
}

export default function ChatUsageNotice({
  usage,
  onNavigate,
}: {
  usage: ChatUsage;
  onNavigate: () => void;
}) {
  const level = chatUsageLevel(usage);
  if (level === CHAT_USAGE_LEVEL.OK) return null;
  const exhausted = level === CHAT_USAGE_LEVEL.EXHAUSTED;
  const upgrade = chatUpgradePlan(usage);
  return (
    <div
      role="status"
      className={`flex gap-2 rounded-xl border px-3 py-2.5 text-xs ${
        exhausted
          ? 'border-danger-200 bg-danger-50 text-danger-800'
          : 'border-warning-200 bg-warning-50 text-warning-800'
      }`}
    >
      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-semibold">
          {exhausted
            ? '今月のAIの利用上限に達しました'
            : `今月の残りが少なくなっています（残り${remainingPercent(usage)}%）`}
        </p>
        <p>
          {formatResetDate(usage.resetsAt)}に回復します。
          {exhausted && '参加予定の確認や使い方など、AIを使わない用途はそのまま使えます。'}
        </p>
        {upgrade && <ChatUpgradeLink plan={upgrade} onNavigate={onNavigate} className="block" />}
      </div>
    </div>
  );
}
